import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useEventos } from "../hooks/useCms";
import { mediaUrl } from "../lib/strapi";
import type { Evento } from "../types/cms";

/**
 * Listado de eventos: /eventos
 *
 * Separamos próximos (fechaInicio en el futuro) de pasados, para que
 * el visitante encuentre primero lo que aún puede atender. Dentro de
 * cada bloque, ordenamos por fecha (próximos: ascendente; pasados:
 * descendente).
 */
export function EventosPage() {
  const { data: eventos, isLoading } = useEventos(96);

  const ahora = Date.now();
  const proximos = (eventos ?? [])
    .filter((e) => new Date(e.fechaInicio).getTime() >= ahora)
    .slice()
    .sort(
      (a, b) =>
        new Date(a.fechaInicio).getTime() - new Date(b.fechaInicio).getTime(),
    );
  const pasados = (eventos ?? [])
    .filter((e) => new Date(e.fechaInicio).getTime() < ahora)
    .slice()
    .sort(
      (a, b) =>
        new Date(b.fechaInicio).getTime() - new Date(a.fechaInicio).getTime(),
    );

  return (
    <>
      <Helmet>
        <title>Eventos — Campus EMJC</title>
        <meta
          name="description"
          content="Eventos, conferencias y actividades puntuales de la Escuela Municipal de Jóvenes Científicos de Extremadura."
        />
      </Helmet>

      <section className="container-emjc py-20">
        <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange-deep">
          Eventos
        </p>
        <h1 className="mt-2 max-w-3xl font-display text-5xl font-semibold leading-tight md:text-6xl">
          Lo que hacemos cuando salimos a la calle.
        </h1>
        <p className="mt-6 max-w-3xl text-lg text-gray-700">
          Conferencias, concursos, jornadas de puertas abiertas y otras
          actividades fuera de los programas regulares. Si tienes pensado
          venir, comprueba aquí lugar y fecha.
        </p>

        {isLoading && (
          <p className="mt-12 text-gray-700">Cargando eventos…</p>
        )}

        {!isLoading && (!eventos || eventos.length === 0) && (
          <div className="mt-12 rounded-2xl border border-gray-300 bg-white p-8">
            <p className="font-display text-xl font-semibold">
              No hay eventos programados ahora mismo.
            </p>
            <p className="mt-3 text-gray-700">
              Publicaremos aquí los próximos en cuanto cerremos fechas.
            </p>
          </div>
        )}

        {proximos.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-3xl font-semibold md:text-4xl">
              Próximos
            </h2>
            <ul className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {proximos.map((e) => (
                <li key={e.documentId ?? e.id}>
                  <EventoCard evento={e} variant="proximo" />
                </li>
              ))}
            </ul>
          </section>
        )}

        {pasados.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display text-3xl font-semibold md:text-4xl">
              Anteriores
            </h2>
            <ul className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {pasados.map((e) => (
                <li key={e.documentId ?? e.id}>
                  <EventoCard evento={e} variant="pasado" />
                </li>
              ))}
            </ul>
          </section>
        )}
      </section>
    </>
  );
}

function EventoCard({
  evento,
  variant,
}: {
  evento: Evento;
  variant: "proximo" | "pasado";
}) {
  const portadaSrc =
    mediaUrl(evento.imagenPortada?.formats?.medium?.url) ??
    mediaUrl(evento.imagenPortada?.url);

  const fechaInicio = new Date(evento.fechaInicio);
  const fechaTxt = fechaInicio.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const horaTxt = fechaInicio.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const lugar = evento.lugar ?? evento.sede?.ciudad;

  return (
    <Link
      to={`/eventos/${evento.slug}`}
      className={`group block overflow-hidden rounded-2xl border bg-white transition focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 ${
        variant === "pasado"
          ? "border-gray-300 opacity-90 hover:border-brand-orange"
          : "border-gray-300 hover:border-brand-orange"
      }`}
    >
      {portadaSrc && (
        <div className="aspect-[4/3] w-full overflow-hidden bg-brand-paper">
          <img
            src={portadaSrc}
            alt={evento.imagenPortada?.alternativeText ?? evento.titulo}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      )}
      <div className="p-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs uppercase tracking-widest text-gray-700">
          <time dateTime={evento.fechaInicio}>{fechaTxt}</time>
          <span aria-hidden="true">·</span>
          <span>{horaTxt}</span>
        </div>
        <h3 className="mt-2 font-display text-xl font-semibold leading-tight">
          {evento.titulo}
        </h3>
        {lugar && (
          <p className="mt-2 text-sm text-gray-700">
            <span className="font-semibold text-brand-ink">Lugar:</span> {lugar}
          </p>
        )}
        <p className="mt-4 text-sm font-semibold text-brand-orange-deep">
          {variant === "proximo" ? "Más información →" : "Ver evento →"}
        </p>
      </div>
    </Link>
  );
}
